import { useEffect } from 'react';

import { AppRoutes } from '@/app/router';
import { applyDocumentLang, applyDocumentTheme, changeLanguage } from '@/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { resolveTheme, useUiStore } from '@/stores/ui.store';

/**
 * The app: three document-level effects, then the route table.
 *
 * Nothing renders behind a guard until `bootstrap()` has resolved — the auth
 * store starts in `unknown` and `RequireAuth` waits it out, which is what keeps
 * a page reload from signing a valid session out.
 */
export function App() {
  useDocumentLanguage();
  useDocumentTheme();
  useSessionBootstrap();

  return <AppRoutes />;
}

/** Mirror `uiStore.lang` onto i18next and onto <html lang/dir> (05 §6.1). */
function useDocumentLanguage(): void {
  const lang = useUiStore((state) => state.lang);

  useEffect(() => {
    changeLanguage(lang);
  }, [lang]);

  // Apply once on mount too, so a persisted preference is honoured before the
  // first paint of any component that reads `dir`.
  useEffect(() => {
    applyDocumentLang(useUiStore.getState().lang);
  }, []);
}

/** Mirror `uiStore.theme` onto the `dark` class, following the OS in `system`. */
function useDocumentTheme(): void {
  const theme = useUiStore((state) => state.theme);

  useEffect(() => {
    applyDocumentTheme(resolveTheme(theme));

    if (theme !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyDocumentTheme(resolveTheme('system'));
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);
}

/**
 * Silent session restore on boot (05 §2.1): the httpOnly `hadir_rt` cookie is
 * exchanged for an access token, and `GET /auth/me` resolves the actor, before
 * anything renders behind a guard.
 *
 * `bootstrap()` is single-flight, so StrictMode's double effect in development
 * still makes exactly one round trip.
 */
function useSessionBootstrap(): void {
  useEffect(() => {
    if (useAuthStore.getState().status !== 'unknown') return;
    void useAuthStore.getState().bootstrap();
  }, []);
}
